import { SimpleGrid } from '@mantine/core';
import DashboardLayout from 'layouts/dashboard/DashboardLayout';
import DevicesCountCard from 'pages/admin-module/dashboard/cards/DevicesCountCard';
import DevicesWithFaultsCard from 'pages/admin-module/dashboard/cards/DevicesWithFaultsCard';
import RevisionsAfterDeadlineCard from 'pages/admin-module/dashboard/cards/RevisionsAfterDeadlineCard';
import ActiveRevisionsCard from 'pages/admin-module/dashboard/cards/ActiveRevisionsCard';
import RevisionsChartCard from 'pages/admin-module/dashboard/cards/RevisionsChartCard';
import RevisionsTableCard from 'pages/admin-module/dashboard/cards/RevisionsTableCard';
import RevisionPlanTableCard from 'pages/admin-module/dashboard/cards/RevisionPlanTableCard';
import ActionablePriceOffersCard from 'pages/admin-module/dashboard/cards/ActionablePriceOffersCard';
import { useApi } from 'api/api-context';
import RevisionsWithReservationsCard from 'pages/admin-module/dashboard/cards/RevisionsWithReservationsCard';
import RevisionsNotSuitableCard from 'pages/admin-module/dashboard/cards/RevisionsNotSuitableCard';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import useOrganizationModules from 'features/organization-modules/use-organization-modules';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Dashboard page.
 *
 */
export default function DashboardPage() {
  const { isMobile } = useResponsiveDesign();
  const { hasPermissionAnywhere } = useApi();
  const { priceOffers, revisions, forms } = useOrganizationModules();
  const [refreshKey, setRefreshKey] = useState(0);

  const canAssignRevisions = revisions && hasPermissionAnywhere('assign-revisions');
  const canFillOutRevisions = revisions && hasPermissionAnywhere('fill-out-revisions');
  const canBrowseDevices = hasPermissionAnywhere('browse-devices');
  const canAcceptRevisions =
    revisions && (hasPermissionAnywhere('accept-revisions') || hasPermissionAnywhere('accept-own-revisions'));
  const canDealWithRevisions = revisions && hasPermissionAnywhere('*-revisions');
  const canDealWithForms = forms && hasPermissionAnywhere('*-forms');
  const canDealWithPriceOffers = priceOffers && hasPermissionAnywhere('*-price-offers');

  /**
   * Triggers a refresh of dashboard cards when data changes.
   */
  const handleDataChange = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const cards = useMemo(() => {
    const cards: ReactNode[] = [];

    if (canAssignRevisions || canFillOutRevisions) {
      cards.push(<ActiveRevisionsCard key={`ActiveRevisionsCard-${refreshKey}`} />);
    }

    if (canAssignRevisions || canFillOutRevisions) {
      cards.push(<RevisionsAfterDeadlineCard key={`RevisionsAfterDeadlineCard-${refreshKey}`} />);
    }

    if (canBrowseDevices) {
      cards.push(<DevicesCountCard key="DevicesCountCard" />);
    }

    if (canAssignRevisions || canAcceptRevisions) {
      cards.push(<RevisionsWithReservationsCard key={`RevisionsWithReservationsCard-${refreshKey}`} />);
    }

    if (canAssignRevisions || canAcceptRevisions) {
      cards.push(<RevisionsNotSuitableCard key={`RevisionsNotSuitableCard-${refreshKey}`} />);
    }

    if (canBrowseDevices) {
      cards.push(<DevicesWithFaultsCard key="DevicesWithFaultsCard" />);
    }

    return cards;
  }, [canAssignRevisions, canFillOutRevisions, canBrowseDevices, canAcceptRevisions, refreshKey]);

  const cols = useMemo(() => {
    if (cards.length === 6) {
      return isMobile ? 2 : 3;
    }

    if (cards.length === 4) {
      return 2;
    }

    return 1;
  }, [cards, isMobile]);

  return (
    <DashboardLayout breadcrumbs={[{ title: 'Riadiaci panel' }]} title="Riadiaci panel" height="auto">
      <SimpleGrid cols={2} spacing={16} breakpoints={[{ maxWidth: 1025, cols: 1 }]}>
        {cards.length > 0 && (
          <SimpleGrid cols={cols} spacing={16}>
            {cards}
          </SimpleGrid>
        )}
        {(canAssignRevisions || canAcceptRevisions) && <RevisionsChartCard key={`RevisionsChartCard-${refreshKey}`} />}
        {(canDealWithRevisions || canDealWithForms) && (
          <RevisionsTableCard
            enabledKinds={[
              ...(canDealWithRevisions ? (['revision'] as const) : []),
              ...(canDealWithForms ? (['form'] as const) : []),
            ]}
            onDataChange={handleDataChange}
          />
        )}
        {revisions && canBrowseDevices && <RevisionPlanTableCard />}
        {canDealWithPriceOffers && <ActionablePriceOffersCard onDataChange={handleDataChange} />}
      </SimpleGrid>
    </DashboardLayout>
  );
}
